import type { MindItem } from '@kimuno/api-sdk';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import config from 'config';
import React from 'react';
import { useTranslation } from 'react-i18next';

import EmptyCheckbox from '../../../../../assets/lernsets/icons/checkbox.svg';
import SelectedCheckbox from '../../../../../assets/lernsets/icons/selectedCheckbox.svg';
import { customSizes, palette } from '../../../../../assets/styles/themes/tokens';
import { colors } from '../../../../../assets/styles/themes/variables';
import useScreenSize from '../../../../../utils/useScreenSize';
import CustomDialog from '../../../../Dialog/CustomDialog';
import { MindItemPageSelectionDialogContent } from '../../../../MindItemPageSelectionDialog';
import { useReaderContext } from '../../../../Reader/ReaderContext';
import { PageSelectionMode } from '../../../Dialog/Views/AIAssistantPageSelector';

const maxDocumentPagesCount = config.ai.readerChat.maxDocumentPagesCount;

interface AIAssistantPageSelectionDialogProps {
	open: boolean;
	handleClose: () => void;
	mindItem: MindItem<'document'>;
	selectedPages: number[];
	handlePageClick: (page: number) => void;
	handleSelectAllPages: (selected: boolean) => void;
	handleSubmit: () => void;
	handleReset: () => void;
	pageSelectionMode: PageSelectionMode;
}
export const AIAssistantPageSelectionDialog = ({
	open,
	handleClose,
	mindItem,
	selectedPages,
	handlePageClick,
	handleSelectAllPages,
	handleSubmit,
	handleReset,
	pageSelectionMode,
}: AIAssistantPageSelectionDialogProps) => {
	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();

	const { selectedPagesForAI } = useReaderContext();

	const resolveSelectAllLabel = () => {
		if (mindItem.pageCount <= maxDocumentPagesCount) {
			return t('Select all pages');
		}
		return t('Select all pages (only {{count}} pages available)', {
			count: maxDocumentPagesCount,
		});
	};

	const isDefaultMode = pageSelectionMode === PageSelectionMode.DEFAULT;
	const isCustomMode = pageSelectionMode === PageSelectionMode.CUSTOM;

	const selectedPagesStr = selectedPages.slice().sort().join(',');
	const documentPagesStr = selectedPagesForAI.slice().sort().join(',');

	const disableSubmitButton =
		(isDefaultMode && selectedPages.length === 0) ||
		(isCustomMode && selectedPagesStr === documentPagesStr);

	if (!open) return null;

	return (
		<CustomDialog
			dataTestId="ai-assistant-page-selection-dialog"
			onClose={handleClose}
			open={open}
			dialogProps={{
				sx: {
					zIndex: 1401,
					'& .MuiDialog-paper': {
						maxWidth: customSizes.exportDialog.pagesSelectionStageWidth,
					},
				},
			}}
			title={t('Select pages for AI application')}
			subTitle={
				mindItem.pageCount > maxDocumentPagesCount
					? t('You can select up to {{count}} pages', {
							count: maxDocumentPagesCount,
						})
					: undefined
			}
			dialogSubTitleSx={{
				color:
					selectedPages.length === maxDocumentPagesCount ? palette.error.main : palette.text.secondary,
			}}
			dialogContentSx={{ borderBottom: `1px solid ${colors.neutral200}` }}
			primaryButton={{
				variant: 'contained',
				color: 'primary',
				size: 'large',
				disabled: disableSubmitButton,
				onClick: () => {
					handleSubmit();
				},
				label: t('Apply'),
			}}
			backButton={{
				variant: 'text',
				color: 'primary',
				size: 'large',
				disabled: !selectedPages.length,
				label: isXsScreen ? t('Reset') : t('Reset selection'),
				onClick: handleReset,
			}}
		>
			<FormControlLabel
				control={
					<Checkbox
						icon={<EmptyCheckbox />}
						checkedIcon={<SelectedCheckbox />}
						checked={selectedPages.length === Math.min(maxDocumentPagesCount, mindItem.pages.length)}
						onChange={(e) => {
							handleSelectAllPages(e.target.checked);
						}}
					/>
				}
				label={resolveSelectAllLabel()}
				labelPlacement="end"
				data-testid="select-all-checkbox"
			/>
			<Box
				sx={{
					overflowX: 'auto',
					width: '100%',
				}}
			>
				<MindItemPageSelectionDialogContent
					handlePageClick={handlePageClick}
					mindItemPages={mindItem.pages}
					selectedPages={selectedPages}
				/>
			</Box>
		</CustomDialog>
	);
};
